import { useCallback, useEffect, useRef } from 'react';
import { Flex, RootScreen, useScreenSize } from '@banhala/ui-kit';
import { useOpenDeeplink } from 'shared/src/hooks';
import { useLogAnalyticsEvent } from 'shared/src/hooks/useLogAnalyticsEvent';
import { useSearchResultRoute } from './hook/useSearchResultRoute';
import { SearchAnalyticsProvider } from './components/SearchAnalyticsProvider';
import { SearchResultHeader } from './components/SearchResultHeader';
import { SearchAssistantView } from './components/SearchAssistantView/SearchAssistantView';
import { RelatedKeywordList } from './components/RelatedKeywordList/RelatedKeywordList';
import { useAssistantInfo } from './queries/useAssistantInfo';
import { useRelatedKeywords } from './queries/useRelatedKeywords';
import { isAndroid, isWeb } from 'shared/src/utils';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { SearchStackParamList } from '../../Navigator';
import { SearchResultList } from './components/SearchResultList';
import {
  SearchType,
  SearchTypeLiteral,
} from 'shared/src/api/enums/SearchType';
import { FilterListAtomicProvider } from 'shared/src/component';
import { DESKTOP_MAX_WIDTH } from 'shared/src/constants/layout';
import { useScrollDirectionHeader } from './hook/useScrollDirectionHeader';
import { calculateNFDCount } from '../../utils/nfdCalculator';
import Animated from 'react-native-reanimated';

const SearchResultInner = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<SearchStackParamList>>();
  const { openDeeplink } = useOpenDeeplink();
  // 부모 SearchResult가 감싼 <AnalyticsScreen screen="SEARCH_RESULT">에 의해
  // useLogAnalyticsEvent가 SCREEN: 'SEARCH_RESULT'를 자동 주입
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const { params } = useSearchResultRoute<'SearchResult'>();

  const currentQuery = params.query ?? '';
  const currentSearchType = (params.search_type ?? SearchType.DIRECT) as SearchType;

  // iOS: SPELL_ORIGINAL / SPELL_SUGGESTION 타입이면 오타 교정 UI 미노출
  const isSpellCorrection =
    currentSearchType === SearchType.SPELL_ORIGINAL ||
    currentSearchType === SearchType.SPELL_SUGGESTION;

  const { data: assistantData } = useAssistantInfo(
    isSpellCorrection ? '' : currentQuery,
  );
  const { data: relatedData } = useRelatedKeywords(
    currentQuery,
    currentSearchType,
  );

  const assistantInfo = isSpellCorrection
    ? null
    : (assistantData?.item ?? null);
  const relatedKeywords = relatedData?.recommends ?? [];

  const convertedKeyword = relatedData?.converted_search_keyword ?? null;

  const { scrollY, collapsibleStyle, onLayout, evaluateDirectionAndroid } =
    useScrollDirectionHeader();
  const { width: screenWidth } = useScreenSize();
  const contentWidth = Math.min(screenWidth, DESKTOP_MAX_WIDTH);
  const handleScrollY = useCallback((y: number) => {
    scrollY.value = y;
  }, [scrollY]);

  // Android: 매 프레임 방향 감지 시 jank 발생. 스크롤 종료 시점(onMomentumEnd / onScrollEndDrag)
  // 에 1회만 방향 판단하도록 worklet 핸들러를 AtomicRenderer에 전달한다.
  // iOS는 useAnimatedReaction 매 프레임 흐름을 그대로 사용 → 핸들러 호출돼도 noop.
  const handleScrollEndWorklet = useCallback(
    (y: number) => {
      'worklet';
      if (isAndroid) {
        evaluateDirectionAndroid(y);
      }
    },
    [evaluateDirectionAndroid],
  );
  const hasCollapsibleContent =
    assistantInfo !== null || relatedKeywords.length > 0;

  // iOS: 연관검색어 노출 시 DISPLAY_ASSOCIATED_SEARCH_KEYWORD 발송
  const hasLoggedDisplay = useRef(false);
  useEffect(() => {
    const recommends = relatedData?.recommends ?? [];
    if (recommends.length > 0 && !hasLoggedDisplay.current) {
      hasLoggedDisplay.current = true;
      logAnalyticsEvent({
        name: 'DISPLAY_ASSOCIATED_SEARCH_KEYWORD',
        channel: ['firebase', 'amplitude', 'facebook', 'ablylog'],
        analytics: {
          SEARCH_KEYWORD: convertedKeyword ?? currentQuery,
          CONVERTED_SEARCH_KEYWORD: convertedKeyword,
          ORIGINAL_SEARCH_KEYWORD: currentQuery,
          SEARCH_TYPE: SearchTypeLiteral[currentSearchType],
          ASSOCIATED_KEYWORD: recommends.map(k => k.display_keyword),
          ASSOCIATED_KEYWORD_COUNT: recommends.length,
          POSITION: 'TOP',
        },
      });
    }
  }, [relatedData, convertedKeyword, currentQuery, currentSearchType, logAnalyticsEvent]);

  /**
   * SearchHelper로 복귀 — react-navigation 스택 내 pop.
   * SearchHelper(검색보조) → push(SearchResult) 로 진입했으므로 같은 스택에 존재.
   * popTo 로 그 인스턴스까지 pop 하면서 검색어를 route param 으로 머지(같은 인스턴스
   * 유지 보장 → unmount 방지). 검색보조가 스택에 없는 직접 진입(예: CATEGORY_KEYWORD)
   * 이거나 web 이면 navigate 로 이동(없으면 push, 있으면 해당 인스턴스로 복귀).
   */
  const goToSearchHelper = useCallback(
    (query: string, showKeyboard: 'true' | 'false') => {
      const helperParams = { query, show_keyboard: showKeyboard };
      if (!isWeb && navigation.canGoBack()) {
        navigation.popTo('SearchHelper', helperParams, { merge: true });
        return;
      }
      navigation.navigate('SearchHelper', helperParams);
    },
    [navigation],
  );

  /** 검색바 클릭 → SearchHelper 복귀 (현재 검색어 유지, 키보드 노출) */
  const handlePressSearchBar = () => goToSearchHelper(currentQuery, 'true');

  /** Clear 버튼 클릭 → SearchHelper 복귀 (검색어 비움) */
  const handlePressClear = () => goToSearchHelper('', 'false');

  /** 이미지 검색 */
  const handlePressImageSearch = async () => {
    logAnalyticsEvent({
      name: 'CLICK_IMAGE_SEARCH',
      channel: ['firebase', 'amplitude', 'facebook', 'ablylog'],
      analytics: {},
    });

    const deeplink =
      'ably://webview?url=https://m.a-bly.com/image-search&title_bar=false';
    openDeeplink(deeplink);
  };

  /** 오타 교정 - 원본 검색어로 재검색 */
  const handlePressOriginalKeyword = async (keyword: string) => {
    const corrected = assistantInfo?.corrected_query;
    const searchType = SearchType.SPELL_ORIGINAL;

    // iOS: clickAssistantKeyword → CLICK_ORIGINAL_SEARCH_KEYWORD
    logAnalyticsEvent({
      name: 'CLICK_ORIGINAL_SEARCH_KEYWORD',
      channel: ['firebase', 'amplitude', 'facebook', 'ablylog'],
      analytics: {
        SEARCH_TYPE: SearchTypeLiteral[searchType],
        SEARCH_KEYWORD: keyword,
        CONVERTED_SEARCH_KEYWORD: corrected,
        ORIGINAL_SEARCH_KEYWORD: keyword,
        REFERRER_SEARCH_KEYWORD: corrected ?? keyword,
      },
      ably_only_log: {
        event_name: 'keyword_search',
        list_id: searchType.toLowerCase(),
        search_query: keyword,
      },
    });
    // iOS: assistantSearchKeyword → SEARCH_KEYWORD
    logAnalyticsEvent({
      name: 'SEARCH_KEYWORD',
      channel: ['firebase', 'amplitude', 'facebook', 'ablylog'],
      analytics: {
        SEARCH_TYPE: SearchTypeLiteral[searchType],
        SEARCH_KEYWORD: keyword,
        CONVERTED_SEARCH_KEYWORD: corrected,
        ORIGINAL_SEARCH_KEYWORD: keyword,
        SEARCH_KEYWORD_KEYSTROKE_COUNT: calculateNFDCount(keyword),
        REFERRER_SEARCH_KEYWORD: corrected ?? keyword,
      },
      ably_only_log: {
        event_name: 'keyword_search',
        list_id: searchType.toLowerCase(),
        search_query: keyword,
      },
    });

    // 현재 SearchResult를 새 SearchResult로 replace(교체) — fresh mount 라
    // 기존 native close+open 과 동일하게 스크롤·once-로깅 ref(DISPLAY_ASSOCIATED_*)가
    // 초기화되고, 스택 길이 불변이라 back 은 그대로 SearchHelper 로 복귀.
    navigation.replace('SearchResult', {
      query: keyword,
      search_type: searchType,
    });
  };

  /** 오타 교정 - 제안 검색어로 재검색 */
  const handlePressSuggestedKeyword = async (keyword: string) => {
    const corrected = assistantInfo?.corrected_query;
    const searchType = SearchType.SPELL_SUGGESTION;

    // iOS: clickAssistantKeyword → CLICK_SUGGESTED_SEARCH_KEYWORD
    logAnalyticsEvent({
      name: 'CLICK_SUGGESTED_SEARCH_KEYWORD',
      channel: ['firebase', 'amplitude', 'facebook', 'ablylog'],
      analytics: {
        SEARCH_TYPE: SearchTypeLiteral[searchType],
        SEARCH_KEYWORD: keyword,
        CONVERTED_SEARCH_KEYWORD: corrected,
        ORIGINAL_SEARCH_KEYWORD: keyword,
        REFERRER_SEARCH_KEYWORD: corrected ?? keyword,
      },
      ably_only_log: {
        event_name: 'keyword_search',
        list_id: searchType.toLowerCase(),
        search_query: keyword,
      },
    });
    // iOS: assistantSearchKeyword → SEARCH_KEYWORD
    logAnalyticsEvent({
      name: 'SEARCH_KEYWORD',
      channel: ['firebase', 'amplitude', 'facebook', 'ablylog'],
      analytics: {
        SEARCH_TYPE: SearchTypeLiteral[searchType],
        SEARCH_KEYWORD: keyword,
        CONVERTED_SEARCH_KEYWORD: corrected,
        ORIGINAL_SEARCH_KEYWORD: keyword,
        SEARCH_KEYWORD_KEYSTROKE_COUNT: calculateNFDCount(keyword),
        REFERRER_SEARCH_KEYWORD: corrected ?? keyword,
      },
      ably_only_log: {
        event_name: 'keyword_search',
        list_id: searchType.toLowerCase(),
        search_query: keyword,
      },
    });

    navigation.replace('SearchResult', {
      query: keyword,
      search_type: searchType,
    });
  };

  /** 연관 검색어 클릭 → SearchResult 재검색 (replace 교체) */
  const handlePressRelatedKeyword = async (
    keyword: {
      raw_keyword: string;
      display_keyword: string;
    },
    index: number,
  ) => {
    const searchType = SearchType.ASSOCIATED;
    const associatedKeywordList = relatedKeywords.map(k => k.display_keyword);

    // iOS: clickAssociatedKeyword → CLICK_ASSOCIATED_SEARCH_KEYWORD
    logAnalyticsEvent({
      name: 'CLICK_ASSOCIATED_SEARCH_KEYWORD',
      channel: ['firebase', 'amplitude', 'facebook', 'ablylog'],
      analytics: {
        CLICK_INDEX: index,
        SEARCH_TYPE: SearchTypeLiteral[searchType],
        CONVERTED_SEARCH_KEYWORD: convertedKeyword,
        ORIGINAL_SEARCH_KEYWORD: currentQuery,
        REFERRER_SEARCH_KEYWORD: currentQuery,
        SEARCH_KEYWORD: keyword.raw_keyword,
        ASSOCIATED_KEYWORD: associatedKeywordList,
        ASSOCIATED_KEYWORD_COUNT: associatedKeywordList.length,
        POSITION: 'TOP',
      },
    });
    // iOS: searchKeyword(model:) → SEARCH_KEYWORD
    logAnalyticsEvent({
      name: 'SEARCH_KEYWORD',
      channel: ['firebase', 'amplitude', 'facebook', 'ablylog'],
      analytics: {
        SEARCH_TYPE: SearchTypeLiteral[searchType],
        SEARCH_KEYWORD: keyword.raw_keyword,
        SEARCH_KEYWORD_KEYSTROKE_COUNT: calculateNFDCount(keyword.raw_keyword),
        CLICK_INDEX: index,
        REFERRER_SEARCH_KEYWORD: currentQuery,
      },
      ably_only_log: {
        event_name: 'keyword_search',
        list_id: searchType.toLowerCase(),
        search_query: keyword.raw_keyword,
      },
    });

    navigation.replace('SearchResult', {
      query: keyword.raw_keyword,
      search_type: searchType,
    });
  };

  return (
    <RootScreen>
      <Flex flex={1}>
      {/* 헤더 (검색바) */}
      <SearchResultHeader
        query={currentQuery}
        onPressSearchBar={handlePressSearchBar}
        onPressClear={handlePressClear}
        onPressImageSearch={handlePressImageSearch}
      />

      {/* 오타 교정 / 키워드 제안 + 연관 검색어 (스크롤 시 숨김) */}
      {/* 웹: SearchResultHeader 와 동일하게 DESKTOP_MAX_WIDTH(600) 인셋·가운데 정렬 */}
      {hasCollapsibleContent && (
        <Animated.View style={collapsibleStyle}>
          <Flex
            onLayout={onLayout}
            alignSelf={isWeb ? 'center' : undefined}
            maxWidth={isWeb ? contentWidth : undefined}
            width={isWeb ? '100%' : undefined}
          >
            {assistantInfo && (
              <SearchAssistantView
                assistantInfo={assistantInfo}
                onPressOriginal={handlePressOriginalKeyword}
                onPressSuggested={handlePressSuggestedKeyword}
              />
            )}
            <RelatedKeywordList
              keywords={relatedKeywords}
              onPress={handlePressRelatedKeyword}
            />
          </Flex>
        </Animated.View>
      )}

      {/* 검색 결과 상품 리스트 */}
      <FilterListAtomicProvider
        preservedParamKeys={['query', 'search_type', 'from_screen', 'screen']}
      >
        <SearchResultList
          screenName="SEARCH_RESULT"
          query={currentQuery}
          onScrollY={handleScrollY}
          onScrollEndWorklet={handleScrollEndWorklet}
        />
      </FilterListAtomicProvider>
      </Flex>
    </RootScreen>
  );
};

const SearchResult = () => {
  const { params } = useSearchResultRoute<'SearchResult'>();
  const currentQuery = params.query ?? '';
  return (
    <SearchAnalyticsProvider screenName="SEARCH_RESULT" query={currentQuery}>
      <SearchResultInner />
    </SearchAnalyticsProvider>
  );
};

export default SearchResult;
